<script setup lang="ts">
import HeaderDefault from "@/components/shared/HeaderDefault.vue";
import { useAuthStore } from "@/stores/auth";
import FooterDefault from "@/components/shared/FooterDefault.vue";
import { useCompanyStore } from "@/stores/company";
import { onMounted, ref } from "vue";
import HeaderLight from "@/components/shared/HeaderLight.vue";
import TermsModal from "@/components/consent/TermsModal.vue";
import { useUserStore } from "@/stores/user";
import type { User } from "@/types";
import useSuperAdmin from "@/components/superadmin/useSuperAdmin";
import { getOnBoardRoute } from "@/router/onBoardingRoute";
import { useRouter } from "vue-router";
import * as Sentry from '@sentry/vue'
import { processUser } from "@/utils/userProcessor";
import { COMPANY_STATUES } from "@/types";

const auth = useAuthStore();
const userStore = useUserStore();
const { showCompanySelectionModal } = useSuperAdmin()
const companyStore = useCompanyStore();
const router = useRouter();

const isSurvey = ref(localStorage.getItem("isSurvey") || 'false');

onMounted(async () => {

  if (userStore.user === null) {
    await userStore.getUser();

    if (userStore.user) {
      const user = userStore.user as User;

      // Sentry user set
      Sentry.setUser({
        id: user.id,
        email: user.email,
        username: user.email,
      });

      // Check if companyId is already set in localStorage (from company selection)
      const storedCompanyId = localStorage.getItem("companyId");

      if (storedCompanyId) {

        userStore.$patch({ companyId: storedCompanyId });
        companyStore.$patch({ companyId: storedCompanyId });

        try {
          await companyStore.getCompany(storedCompanyId);
          await companyStore.fetchOperationYears();

          // Onboarding check - skip for survey routes
          const currentPath = router.currentRoute.value.path;
          const isSurveyRoute = currentPath.startsWith('/survey/');
          
          if (!isSurveyRoute && companyStore.company.onboardingStatus !== COMPANY_STATUES.COMPLETE) {
            const onboardingStatus = companyStore.company.onboardingStatus as COMPANY_STATUES;
            const route = getOnBoardRoute(onboardingStatus);
            router.push({ name: route });
            return;
          }

        } catch (error) {
          console.error('Error loading company data:', error);
          // On error, logout user
          auth.logout();
          return;
        }

        return;
      }

      // If no stored companyId, use the modular user processing system
      const processedUser = processUser(user, showCompanySelectionModal);

      if (processedUser && processedUser.companyId) {
        // Carbon Accounting user or companyId obtained from localStorage
        const companyId = processedUser.companyId;

        userStore.$patch({ companyId: companyId });
        localStorage.setItem("companyId", companyId);
        companyStore.$patch({ companyId: companyId });

        try {
          await companyStore.getCompany(companyId);
          await companyStore.fetchOperationYears();

          // Onboarding check - skip for survey routes
          const currentPath = router.currentRoute.value.path;
          const isSurveyRoute = currentPath.startsWith('/survey/');
          
          if (!isSurveyRoute && companyStore.company.onboardingStatus !== COMPANY_STATUES.COMPLETE) {
            const onboardingStatus = companyStore.company.onboardingStatus as COMPANY_STATUES;
            const route = getOnBoardRoute(onboardingStatus);
            router.push({ name: route });
            return;
          }

        } catch (error) {
          console.error('Error loading company data:', error);
          // On error, logout user
          auth.logout();
          return;
        }

        return;
      }

      // If processUser didn't return companyId and modal wasn't opened
      // Modal should already be open or user doesn't require special role
      if (processedUser && !processedUser.companyId) {
        // If modal is open, wait; if not, check localStorage
        const storedCompanyId = localStorage.getItem("companyId");
        if (storedCompanyId) {
          userStore.$patch({ companyId: storedCompanyId });
          companyStore.$patch({ companyId: storedCompanyId });
        }
      }
    }
  }

  // Load company info if not already loaded
  if (!companyStore.company.companyName) {
    try {
      await companyStore.getCompany();
    } catch (error) {
      console.error('Error loading company:', error);
      // If company can't be loaded, logout user
      auth.logout();
      return;
    }
  }

  // Operation years are non-fatal — never logout if they fail.
  try {
    await companyStore.fetchOperationYears();
  } catch (error) {
    console.error('Error loading operation years:', error);
  }

});
</script>

<template>
  <Suspense>
    <div
      class="flex flex-col min-h-screen justify-between bg-neautrals-50"
      v-if="auth.isLoggedIn"
    >
      <HeaderDefault v-if="isSurvey === 'false'" />
      <HeaderLight v-if="isSurvey === 'true'" />
      <TermsModal />
      <main class="mb-auto mx-12 sm:mx-6 lg:mx-12 py-6">
        <slot />
      </main>
      <FooterDefault />
    </div>
  </Suspense>
  <DynamicDialog />
  <ConfirmDialog/>
  <Toast />
</template>

<style>
:root {
  --toastify-icon-color-success: #212121;
  --toastify-toast-width: 657px;
}
</style>